import { FloatingPortal } from "@floating-ui/react";
import {
  GenericDropdown,
  type ListItemProps,
} from "@studio3t/generic-components";
import { useEffect, useState } from "react";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { useDeleteSchema } from "@/hooks/useApi";
import { schemaRowId, usePipelineStaging } from "./pipelineStaging";
import {
  acquirePreviewSuppression,
  releasePreviewSuppression,
} from "./previewMenuPin";

// Deleting a stored schema from the explorer tree — the suite delete's
// twin (TestPreview.tsx): a confirm-gated backend delete reachable from
// the row's right-click menu and from the hover card's header. A schema
// the pipeline stages is unstaged with it (its attachments go too — the
// entry no longer exists to resolve).

/** A schema row as the delete needs it: its service and store entry id
 *  (parseSchemaRowId), i.e. the row id's parts. */
export type SchemaRef = { serviceName: string; entryId: string };

// The confirm + delete flow for one schema row; `schema` null means the
// row is not a schema (the hooks still run — call order stays fixed).
export function useSchemaDelete(schema: SchemaRef | null) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: deleteSchema } = useDeleteSchema();
  const { roleOf, unstage } = usePipelineStaging();

  // While THIS delete's confirm is up, no hover preview may open anywhere
  // in the tree (same guard as the suite delete).
  useEffect(() => {
    if (!confirming) return;
    acquirePreviewSuppression();
    return () => releasePreviewSuppression();
  }, [confirming]);

  const confirmDelete = async () => {
    if (!schema) {
      setError("Not a stored schema");
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await deleteSchema({
        serviceName: schema.serviceName,
        id: schema.entryId,
      });
      const rowId = schemaRowId(schema.serviceName, schema.entryId);
      if (roleOf(rowId) === "schema") unstage(rowId);
      setConfirming(false);
    } catch {
      setError("Failed to delete schema");
    } finally {
      setDeleting(false);
    }
  };

  return {
    confirming,
    deleting,
    error,
    request: () => {
      setError(null);
      setConfirming(true);
    },
    cancel: () => setConfirming(false),
    confirmDelete,
  };
}

export type SchemaDeleteFlow = ReturnType<typeof useSchemaDelete>;

// The delete confirmation, portaled to the body — rendered in place it
// would sit inside the tree's stacking contexts (see TestPreviewHover).
export function SchemaDeleteModal({
  name,
  flow,
}: {
  name: string;
  flow: SchemaDeleteFlow;
}) {
  return (
    <FloatingPortal>
      <ConfirmDeleteModal
        isOpen={flow.confirming}
        title="Delete schema"
        message={
          <>
            Are you sure you want to delete <strong>{name}</strong>? This action
            cannot be undone.
          </>
        }
        loading={flow.deleting}
        error={flow.error}
        onConfirm={flow.confirmDelete}
        onCancel={flow.cancel}
      />
    </FloatingPortal>
  );
}

/** Right-click menu for a tree schema row: one Delete entry opening the
 *  same confirm-gated delete as the hover card's header button. */
export function SchemaRowContextMenu({
  areaRef,
  schema,
  name,
}: {
  areaRef: React.RefObject<HTMLDivElement>;
  schema: SchemaRef;
  name: string;
}) {
  const flow = useSchemaDelete(schema);
  const items: ListItemProps[][] = [
    [
      {
        id: "delete-schema",
        type: "text",
        value: "delete-schema",
        label: "Delete",
        closeOnSelect: true,
        onClick: flow.request,
      },
    ],
  ];
  return (
    <>
      <GenericDropdown
        asContextMenu={areaRef}
        dropdownItems={items}
        width={{ strategy: "fixed", size: 160 }}
      />
      <SchemaDeleteModal name={name} flow={flow} />
    </>
  );
}
